// Vocabularul comun al ecranelor de soluții: numele disciplinelor, banii,
// progresul unei rulări, timpul scurs.
//
// Tabla cu patru coloane, cardurile și panoul intermediar au plecat
// (`ui/docs/arhitectura_informatiei.md` A10); arborele, numărătorile și
// alegerea soluției stau acum în `nav/selectie.ts`, citite din registru.
// Aici au rămas formatările pe care le folosesc mai multe ecrane.

import type { EtapaJob, Job, MotivAsteptare, ParametriCod, TitluPas } from "../api";
import { esteCheie, numar, ora, t, tn } from "../i18n";
import type { Cheie, CheiePlural } from "../i18n";

const NUME_DISCIPLINA: Record<string, Cheie> = {
  site_plan: "disciplina.site_plan",
  architecture: "disciplina.architecture",
  structure: "disciplina.structure",
};

/** Cum se numește disciplina în propoziție. O cheie necunoscută se scrie așa
 *  cum a venit: e un cod de la server, iar ghicirea unui nume ar fi mai rea. */
export function numeDisciplina(cheie: string): string {
  const c = NUME_DISCIPLINA[cheie];
  return c ? t(c) : cheie;
}

/** Fraza unui text CODAT de job, din catalog, în limba ecranului.
 *
 *  Jobul scrie textul O DATĂ, iar rândul îl citesc oameni în limbi diferite:
 *  fraza se naște deci aici, din cod și parametri — `asteapta.<cod>` pentru ce
 *  așteaptă, `etapa.<cod>` pentru la ce etapă e, `titlu_pas.<cod>` pentru
 *  pasul de structură în lucru. Cheia cu plural
 *  (`<spațiu>.<cod>#n`) se alege după `n`. `null` când ecranul nu știe codul:
 *  apelantul cade atunci pe textul românesc al jobului — un rând scris înainte
 *  de coduri, sau un cod mai nou decât ecranul. E o plasă, nu o cale; codurile
 *  serverului și cheile de aici le ține în pas `i18n/etape.test.ts`. */
function dinCod(spatiu: "asteapta" | "etapa" | "titlu_pas", cod: string | null | undefined,
                param: ParametriCod | null | undefined): string | null {
  if (!cod) return null;
  const plural = `${spatiu}.${cod}#n`;
  if (esteCheie(plural) && typeof param?.n === "number") {
    return tn(plural as CheiePlural, param.n, param);
  }
  const cheie = `${spatiu}.${cod}`;
  return esteCheie(cheie) ? t(cheie, param ?? undefined) : null;
}

/** Ce așteaptă o rulare, în limba ecranului; `null` când jobul n-a spus. */
export function motivAsteptarii(m: MotivAsteptare | null | undefined): string | null {
  return dinCod("asteapta", m?.motiv_cod, m?.motiv_param) ?? (m?.motiv || null);
}

/** La ce etapă e un job, în limba ecranului; `null` când jobul n-a spus. */
export function etapaJobului(j: EtapaJob | null | undefined): string | null {
  return dinCod("etapa", j?.stage_cod, j?.stage_param) ?? (j?.stage || null);
}

/** Titlul pasului în lucru, în limba ecranului: «Step 7 (model_fem)». */
export function titluPasului(p: TitluPas | null | undefined): string | null {
  return dinCod("titlu_pas", p?.titlu_cod, p?.titlu_param) ?? (p?.titlu || null);
}

/** Un job VIU are un proces care macină. `waiting` nu e viu: pasul s-a
 *  terminat și următorul așteaptă alt job. */
export const isJobAlive = (j: Job) =>
  j.status === "running" || j.status === "pending";

/** Explicația din paranteze, scoasă. Numai de pe motivul unei AȘTEPTĂRI: pe
 *  fazele de lucru paranteza nu explică, ci numește — «refac S1 (C1)» e
 *  subsolul S1 al corpului C1, și fără ea rămâne un nume ambiguu. */
const faraParanteze = (text: string) => text.replace(/\s*\([^)]*\)/g, "").trim();

/** Felurile de job al căror `step_index` numără pașii ÎNCHEIAȚI (0/2 cât
 *  rulează pasul 1, 1/2 cât așteaptă și cât rulează pasul 2, 2/2 la final):
 *  pe ele se sprijină control plane-ul (`registru._fazele`, reluarea din
 *  `api/jobs.py`), deci cifra din bază rămâne așa. Structura, în schimb, scrie
 *  deja pasul în lucru (indexul canonic al pasului următor). */
const PASI_INCHEIATI = new Set(["architecture", "site_plan", "basement_architecture"]);

/** Numărul pasului de arătat omului: pe o rulare VIE, pasul care se lucrează
 *  acum, nu câți s-au încheiat. «Subsol (în lucru) · Pasul 2/2», nu «1/2»,
 *  pentru că omul citește cifra ca pe pasul curent, nu ca pe un rest. Pe un job
 *  oprit (așteaptă, gata, picat) cifra rămâne a pașilor încheiați. */
export function pasAfisat(j: { kind: string; status: string;
                               step_index: number | null; step_total: number | null }): number {
  const i = j.step_index ?? 0;
  if (!PASI_INCHEIATI.has(j.kind)) return i;
  if (j.status !== "running" && j.status !== "pending") return i;
  return Math.min(i + 1, j.step_total ?? i + 1);
}

/** «Pasul 13/17», «38 %» or the stage text — whatever the job actually knows.
 *  A run with neither counter nor stage still says something ("în lucru"): an
 *  empty chip reads as a stuck run. */
export function jobProgress(job: Job | null): string {
  if (!job) return t("job.in_lucru");
  // O AȘTEPTARE n-are pași, are un motiv: ce job trebuie să se încheie ca
  // rularea să continue. Pe pastilă rămâne CE așteaptă; DE CE se citește la
  // hover, din motivul întreg.
  if (job.status === "waiting") {
    return faraParanteze(motivAsteptarii(job.asteapta) ?? t("job.asteapta"));
  }
  // ARHITECTURA are doi pași: 1/2 cât se compartimentează etajele, 2/2 cât se
  // umple subsolul (v. `pasAfisat`). Pe pasul în lucru spune și `stage` —
  // subsolurile se rezolvă deodată, deci numai el se mișcă.
  if (job.step_total) {
    const i = pasAfisat(job), n = job.step_total;
    const etapa = etapaJobului(job);
    if ((job.kind === "architecture" || job.kind === "site_plan") && etapa) {
      return t("job.pasi_faza", { i, n, faza: etapa });
    }
    return t("job.pasul", { i, n });
  }
  const etapa = etapaJobului(job);
  if (etapa) return etapa;
  return t(job.status === "pending" ? "job.in_coada" : "job.in_lucru");
}

/** Money the way the lists show it: 4,82 M€ over a million, 195 k€ under it.
 *  The third digit of a structure cost is noise anyway — it moves with the
 *  steel price. */
export function money(value: number): string {
  if (!Number.isFinite(value)) return "—";
  if (Math.abs(value) >= 1e6) {
    return `${numar(value / 1e6,
      { minimumFractionDigits: 2, maximumFractionDigits: 2 })} M€`;
  }
  return `${numar(value / 1000, { maximumFractionDigits: 0 })} k€`;
}

/** «acum», «de 4 min» — a search on a big parcel really does take minutes, and
 *  a screen that does not say for how long looks like a stuck one. */
export function since(iso: string | null | undefined): string {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (s < 45) return t("timp.acum");
  if (s < 5400) return t("timp.minute", { n: Math.round(s / 60) });
  return t("timp.ore", { n: Math.round(s / 3600) });
}

/** hh:mm:ss of a feed entry, in the mono uppercase the feed uses. */
export function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : ora(d);
}
